import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { t } from "ttag";

import { getWeightEntriesQuery } from "@/app/api/legacy-api";
import { CreateWeightEntry } from "@/app/components/legacy-modals";
import { UnitSystemSwitch } from "@/shared/components";
import { useBoolean } from "@/shared/hooks";
import { NOW, PERIOD } from "@/shared/models/legacy-constants";
import type { Period } from "@/shared/models/legacy-types";
import { Button, Card, Select } from "@/shared/ui";
import { formatToBackendDate, parseBackendDate } from "@/shared/utils";
import { WeightEntriesContext } from "./context";
import {
  ChartEmpty,
  ChartSkeleton,
  PeriodPicker,
  WeightProgressChart,
} from "./sections";

const getViewOptions = () =>
  [
    { value: PERIOD.MONTHLY, label: t`Monthly View` },
    { value: PERIOD.YEARLY, label: t`Yearly View` },
  ] as const;

export const WeightProgress = () => {
  const [selectedDate, setSelectedDate] = useState(NOW);
  const [view, setView] = useState<Period>(PERIOD.MONTHLY);

  const {
    value: showCreateWeightEntryModal,
    setFalse: closeCreateWeightEntryModal,
    setTrue: openCreateWeightEntryModal,
  } = useBoolean();

  const { data, isLoading, isSuccess } = useQuery({
    ...getWeightEntriesQuery({
      date: formatToBackendDate(selectedDate),
      periodType: view,
    }),
    select: (data) =>
      data.map((d) => ({
        ...d,
        x:
          view === PERIOD.MONTHLY
            ? parseBackendDate(d.date).getDate()
            : parseBackendDate(d.date).getMonth() + 1,
        y: d.weight,
      })),
  });

  return (
    <Card.Root className="grid gap-y-4 xl:grid-cols-[1fr_auto_auto] xl:p-8">
      <Card.Title>{t`Weight Progress Tracker`}</Card.Title>

      <div className="flex gap-4 self-end">
        <Select
          id="chart-view-mode"
          value={view}
          onChange={setView}
          options={getViewOptions()}
          className="xl:max-w-48"
        />

        <UnitSystemSwitch />
      </div>

      <Button
        size="lg"
        onClick={openCreateWeightEntryModal}
        className="row-start-4 xl:row-auto"
      >
        {t`Log data`}
      </Button>

      <Card.Content className="col-span-full rounded-lg bg-white">
        <div className="flex w-full justify-center p-4 pb-2.5 xl:justify-end">
          <PeriodPicker
            mode={view}
            date={selectedDate}
            onChange={setSelectedDate}
          />
        </div>

        {isLoading && <ChartSkeleton />}

        {isSuccess && !data.length && <ChartEmpty />}

        {isSuccess && data.length > 0 && (
          <WeightEntriesContext.Provider value={{ data, view }}>
            <WeightProgressChart />
          </WeightEntriesContext.Provider>
        )}
      </Card.Content>

      <CreateWeightEntry
        open={showCreateWeightEntryModal}
        onOpenChange={closeCreateWeightEntryModal}
      />
    </Card.Root>
  );
};
